import {
  useSalesServiceSalesInvoicesBrokerPayCreate,
  useSalesServiceSalesInvoicesSendPaymentLinkCreate,
  useSalesServiceSalesInvoicesStaffPayCreate,
  useSalesServiceSalesOrdersModificationPriceCreate,
  useSalesServiceSalesOrdersModifyCreate,
  useSalesServiceSalesProductsAccidentsOrderBrokerCreate,
  useSalesServiceSalesProductsAccidentsOrderStaffCreate,
  useSalesServiceSalesProductsPriceCreate
} from '@insurance/api/queries'
import {
  ContractorType,
  InvoiceDetail,
  OrderInspectionStatus,
  OrderProduct,
  PaymentTypeStaff
} from '@insurance/api/requests'
import {
  PayBrokerButton,
  PaymentExtendType,
  PaymentValue,
  PayStaffButton,
  RefundMoneyButton
} from '@insurance/components'
import { useAppNavigate, useModifyPayment } from '@insurance/hooks'
import { paths } from '@insurance/routes'
import { ProductCode } from '@insurance/types'
import { DEFAULT_CURRENCY } from '@shared/constants'
import { useSessionContext } from '@shared/hooks'
import {
  getFormatCurrency,
  replaceEmptyWithUndefined,
  showApiErrors
} from '@shared/utils'
import { App, Button, Flex, Form, Modal } from 'antd'
import { useTranslation } from 'react-i18next'

import { useAccidentInsuranceValues } from './AccidentInsuranceContext'
import {
  formProps,
  normalizeCommonData,
  normalizeModifyOrderData,
  normalizeOrderData,
  removeAccidentValuesFromStorage
} from './utils'

const { confirm } = Modal

type Props = {
  steps: number
  onReadyToSaveData?: () => void
}

export const AccidentInsuranceControls = ({
  steps,
  onReadyToSaveData
}: Props) => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const { stepKey, priceKey, prevStepKey, risksKey, contractorProps } =
    formProps(t)
  const { message } = App.useApp()
  const step = Form.useWatch(stepKey.name, { preserve: true })
  const price = Form.useWatch(priceKey.name, { preserve: true })
  const contractor = Form.useWatch(contractorProps.name, { preserve: true })
  const { broker } = useSessionContext()
  const navigate = useAppNavigate()
  const { isEditable, order } = useAccidentInsuranceValues()

  const isLegalEntity = contractor?.content_type === ContractorType.LEGALENTITY

  const hasPrevStep = step > 0
  const isCalculateStep = step === 0
  const isPreparePersons = step === 1
  const isPayStep = step === steps - 1

  const handleFinishStepError = (error: unknown) => {
    showApiErrors(error, formInstance)
  }

  const handleSuccessCommonPayment = () => {
    removeAccidentValuesFromStorage()
    message.success(t('insurance_created_successfully'))
  }

  const { mutate: calculate, isPending: isCalculatingLoading } =
    useSalesServiceSalesProductsPriceCreate({
      onSuccess: (data) => {
        handleChangeStep(step + 1)

        formInstance.setFieldValue(priceKey.name, {
          currency: DEFAULT_CURRENCY,
          amount: Number(Number(data?.amount)?.toFixed(2))
        })
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      onError: handleFinishStepError
    })

  const { mutate: calculateModify, isPending: isCalculatingModifyLoading } =
    useSalesServiceSalesOrdersModificationPriceCreate({
      onSuccess: (data) => {
        handleChangeStep(step + 1)

        formInstance.setFieldValue(priceKey.name, {
          currency: DEFAULT_CURRENCY,
          amount: Number(Number(data?.price_data?.amount)?.toFixed(2))
        })
        window.scrollTo({ top: 0, behavior: 'smooth' })
      },
      onError: handleFinishStepError
    })

  const {
    mutate: calculateModifyRefund,
    isPending: isCalculatingModifyRefundLoading
  } = useSalesServiceSalesOrdersModificationPriceCreate({
    onSuccess: (data) => {
      formInstance.setFieldValue(priceKey.name, {
        currency: DEFAULT_CURRENCY,
        amount: Number(Number(data?.price_data?.amount)?.toFixed(2))
      })
    },
    onError: handleFinishStepError
  })

  const { mutate: payBroker, isPending: isBrokerPayLoading } =
    useSalesServiceSalesInvoicesBrokerPayCreate({
      onSuccess: ({ invoice }) => {
        handleSuccessCommonPayment()
        navigate(
          invoice?.order.id
            ? paths.orders.details(invoice.order.id)
            : paths.salesPanel.root
        )
      },
      onError: handleFinishStepError
    })

  const { mutate: pay, isPending: isPayLoading } =
    useSalesServiceSalesInvoicesStaffPayCreate({
      onSuccess: ({ invoice }) => {
        handleSuccessCommonPayment()
        navigate(
          invoice?.order.id
            ? paths.orders.details(invoice.order.id)
            : paths.salesPanel.root
        )
      },
      onError: (error, variables) => {
        handleFinishStepError(error)

        const isPayCash =
          variables?.requestBody?.payment_type === PaymentTypeStaff.CASH

        const errorStatus = (error as { status?: number })?.status

        if (isPayCash && errorStatus !== 500) {
          confirm({
            title: t('error_payment'),
            content: t('error_payment_confirmation'),
            okText: t('pay_manually'),
            onOk: () => {
              pay({
                id: variables.id,
                requestBody: {
                  ...variables.requestBody,
                  force_manual: true
                }
              })
            }
          })
        }
      }
    })

  const { mutate: initBrokerOrder, isPending: isInitBrokerOrderLoading } =
    useSalesServiceSalesProductsAccidentsOrderBrokerCreate({
      onError: handleFinishStepError
    })

  const { mutate: initStaffOrder, isPending: isInitStaffOrderLoading } =
    useSalesServiceSalesProductsAccidentsOrderStaffCreate({
      onError: handleFinishStepError
    })

  const { mutate: initModifyOrder, isPending: isInitModifyOrderLoading } =
    useSalesServiceSalesOrdersModifyCreate({
      onError: handleFinishStepError
    })

  const { mutate: paymentLink, isPending: isPaymentLinkLoading } =
    useSalesServiceSalesInvoicesSendPaymentLinkCreate({
      onSuccess: handleSuccessCommonPayment,
      onError: handleFinishStepError
    })

  const { onFinishPay, payPermission, isPaymentLoading } = useModifyPayment({
    formInstance
  })

  const handleChangeStep = (currentStep: number) => {
    formInstance.setFieldValue(stepKey.name, currentStep)
    formInstance.setFieldValue(prevStepKey.name, step)
    onReadyToSaveData?.()
  }

  const handlePrevStep = () => {
    const prevStep = step - 1

    handleChangeStep(prevStep)

    if (!prevStep && !isEditable) {
      formInstance.setFieldValue(priceKey.name, undefined)
      formInstance.setFieldValue(risksKey.name, undefined)
    }
  }

  const handleCalculate = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (!order?.id) {
      const bodyValues = await normalizeCommonData(values)
      calculate({ code: ProductCode.ACCIDENTS, requestBody: bodyValues })
    } else {
      handleChangeStep(step + 1)

      formInstance.setFieldValue(priceKey.name, {
        currency: DEFAULT_CURRENCY,
        amount: Number(
          Number(price?.amount || order.full_paid_amount)?.toFixed(2)
        )
      })
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleGoToCustomer = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (!order?.id) {
      const bodyValues = await normalizeCommonData(values, true)
      calculate({ code: ProductCode.ACCIDENTS, requestBody: bodyValues })
    } else {
      const bodyValues = await normalizeModifyOrderData(values)

      calculateModify({ requestBody: bodyValues!, id: order?.id })
    }
  }

  const handleCalculateRefund = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)
    const bodyValues = await normalizeModifyOrderData(values)

    if (order?.id)
      calculateModifyRefund({ requestBody: bodyValues!, id: order?.id })
  }

  const handleInitBrokerOrder = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    const handleSuccess = ({ invoice }: OrderProduct) => {
      if (!invoice?.id) return

      payBroker({
        id: invoice.id
      })
    }

    const bodyValues = await normalizeOrderData(values)

    initBrokerOrder(
      { requestBody: replaceEmptyWithUndefined(bodyValues) },
      {
        onSuccess: handleSuccess
      }
    )
  }

  const handleInitStaffOrder = async (paymentValue: PaymentValue) => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    const handleSuccess = (invoice: InvoiceDetail | null) => {
      if (!invoice?.id) return

      if (paymentValue.paymentType !== PaymentExtendType.ONLINE_PAYMENT)
        pay({
          id: invoice.id,
          requestBody: {
            payment_type: paymentValue.paymentType,
            cash_box: paymentValue.cashBox
          }
        })

      if (paymentValue.paymentType === PaymentExtendType.ONLINE_PAYMENT)
        paymentLink(
          { id: invoice.id },
          {
            onSuccess: () => {
              navigate(
                invoice.order.id
                  ? paths.orders.details(invoice.order.id)
                  : paths.salesPanel.root
              )
            }
          }
        )
    }

    if (!order?.id) {
      const bodyValues = await normalizeOrderData(values)
      initStaffOrder(
        { requestBody: replaceEmptyWithUndefined(bodyValues) },
        {
          onSuccess: ({ invoice }) => handleSuccess(invoice)
        }
      )
    } else {
      const bodyValues = await normalizeModifyOrderData(values)
      initModifyOrder(
        {
          id: order?.id,
          requestBody: replaceEmptyWithUndefined({
            ...bodyValues,
            status: OrderInspectionStatus.OFFER_READY
          })
        },
        {
          onSuccess: (order) => onFinishPay(order.id!, handleSuccess)
        }
      )
    }
  }

  const handleOnlyModify = async () => {
    await formInstance.validateFields()
    const values = formInstance.getFieldsValue(true)

    if (!order?.id) return

    const bodyValues = await normalizeModifyOrderData(values)
    initModifyOrder(
      {
        id: order?.id,
        requestBody: replaceEmptyWithUndefined({
          ...bodyValues,
          status: OrderInspectionStatus.OFFER_READY
        })
      },
      {
        onSuccess: (order) =>
          onFinishPay(order.id!, () => {
            navigate(
              order.id ? paths.orders.details(order.id) : paths.salesPanel.root
            )
          })
      }
    )
  }

  const orderPrice = {
    amount: order?.full_paid_amount ? Number(order.full_paid_amount) : 0
  }
  const canPay = !isEditable || (!broker && price?.amount > orderPrice?.amount)
  const canRefund = isEditable && orderPrice?.amount > price?.amount
  const canModify = isEditable && orderPrice?.amount === price?.amount

  return (
    <Flex justify="end" gap={12}>
      {hasPrevStep && (
        <Button color="primary" variant="outlined" onClick={handlePrevStep}>
          {t('prev_step')}
        </Button>
      )}

      {isCalculateStep && (
        <Button
          type="primary"
          loading={isCalculatingLoading || isCalculatingModifyLoading}
          onClick={handleCalculate}
        >
          {t('calculate')}
        </Button>
      )}

      {isPreparePersons && (
        <Button
          type="primary"
          loading={isCalculatingLoading || isCalculatingModifyLoading}
          onClick={handleGoToCustomer}
        >
          {t('next_step')}
        </Button>
      )}

      {isPayStep && !broker && canPay && (
        <PayStaffButton
          disabled={isEditable && !payPermission}
          price={getFormatCurrency({ amount: price?.amount })}
          loading={
            isInitStaffOrderLoading ||
            isPayLoading ||
            isPaymentLinkLoading ||
            isInitModifyOrderLoading ||
            isPaymentLoading
          }
          isLegalEntity={isLegalEntity}
          onPay={handleInitStaffOrder}
        />
      )}

      {isPayStep && broker && canPay && (
        <PayBrokerButton
          loading={isInitBrokerOrderLoading || isBrokerPayLoading}
          onPay={handleInitBrokerOrder}
        />
      )}

      {canModify && isPayStep && (
        <Button
          type="primary"
          loading={isInitModifyOrderLoading}
          onClick={handleOnlyModify}
        >
          {t('modify_insurance_menu')}
        </Button>
      )}

      {canRefund && isPayStep && (
        <RefundMoneyButton
          loading={isCalculatingModifyRefundLoading}
          onChangeRefund={handleCalculateRefund}
          onRefund={handleOnlyModify}
        />
      )}
    </Flex>
  )
}
